import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, AlertCircle } from 'lucide-react';
import { DateTime } from 'luxon';

import { Button } from '@/components/Button';
import { Sheet } from '@/components/Sheet';
import { cn } from '@/lib/cn';
import { api, ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useClients, useProjects, taskKeys } from '@/features/tasks/useTasks';
import type { Task } from '@/features/tasks/types';
import { formatDuration } from '@/features/tasks/tree';
import { useFocus, useFocusControls } from '@/features/time/useFocus';
import { formatClock } from '@/features/time/useTimer';
import { EventLocation } from './EventLocation';

export interface PlanEvent {
  id: number;
  calendar_id: number;
  title: string;
  description: string | null;
  location: string | null;
  starts_at: string;
  ends_at: string;
  starts_tz?: string | null;
  all_day: boolean;
  counts_as_busy: boolean;
  /** When false, Plan shows every event from this calendar as free. */
  calendar_is_busy_source: boolean;
  client_id: number | null;
  project_id: number | null;
  billable: boolean;
  completed_at: string | null;
  color: string | null;
  is_recurring: boolean;
  /** Calendar's default prep-task length, in minutes. */
  prep_minutes?: number;
  status?: string;
}

interface Props {
  event: PlanEvent;
  timezone: string;
  onClose: () => void;
}

/** Solid fill vs dashed free styling on the Plan grid. */
export function eventShowsAsBusy(
  event: Pick<PlanEvent, 'counts_as_busy' | 'calendar_is_busy_source'>,
): boolean {
  return event.counts_as_busy && event.calendar_is_busy_source;
}

function formatRange(event: PlanEvent, timezone: string): string {
  if (event.all_day) {
    const start = DateTime.fromISO(event.starts_at, { zone: 'utc' }).setZone(timezone);
    const endExclusive = DateTime.fromISO(event.ends_at, { zone: 'utc' }).setZone(timezone);
    const end = endExclusive.minus({ days: 1 });
    if (start.hasSame(end, 'day')) {
      return start.toFormat('ccc, LLL d');
    }
    return `${start.toFormat('LLL d')} – ${end.toFormat('LLL d')}`;
  }
  const start = DateTime.fromISO(event.starts_at, { zone: 'utc' }).setZone(timezone);
  const end = DateTime.fromISO(event.ends_at, { zone: 'utc' }).setZone(timezone);
  return `${start.toFormat('ccc, LLL d · h:mm a')} – ${end.toFormat('h:mm a')}`;
}

function isPastEvent(event: PlanEvent, timezone: string): boolean {
  const end = DateTime.fromISO(event.ends_at, { zone: 'utc' }).setZone(timezone);
  return end < DateTime.now().setZone(timezone);
}

const DEFAULT_PREP_MINUTES = 15;
const PREP_PRESETS = [15, 30, 45, 60];

/**
 * Treat an imported calendar event as a day to-do: assign client/project,
 * billable, done, and free/busy: while showing the upstream details.
 */
export function PlanEventSheet({ event: initial, timezone, onClose }: Props) {
  const qc = useQueryClient();
  const clients = useClients();
  const focus = useFocus();
  const focusControls = useFocusControls();
  const [event, setEvent] = useState(initial);
  const [clientId, setClientId] = useState<number | null>(initial.client_id);
  const [projectId, setProjectId] = useState<number | null>(initial.project_id);
  const [billable, setBillable] = useState(initial.billable);
  const [prepMinutes, setPrepMinutes] = useState(
    initial.prep_minutes ?? DEFAULT_PREP_MINUTES,
  );

  useEffect(() => {
    setEvent(initial);
    setClientId(initial.client_id);
    setProjectId(initial.project_id);
    setBillable(initial.billable);
    setPrepMinutes(initial.prep_minutes ?? DEFAULT_PREP_MINUTES);
  }, [initial]);

  const projects = useProjects(clientId ?? undefined);

  const past = useMemo(() => isPastEvent(event, timezone), [event, timezone]);
  const done = Boolean(event.completed_at);
  const needsCompleteNudge = past && !done;
  const scheduledMinutes = event.all_day
    ? null
    : Math.max(
        1,
        Math.round(
          (new Date(event.ends_at).getTime() - new Date(event.starts_at).getTime()) / 60_000,
        ),
      );

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['calendars', 'events'] });
    void qc.invalidateQueries({ queryKey: ['calendars', 'busy'] });
  };

  const saveWork = useMutation({
    mutationFn: (patch: {
      client_id?: number | null;
      project_id?: number | null;
      billable?: boolean;
    }) =>
      api<PlanEvent>(`/api/calendars/events/${event.id}/work`, {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: (updated) => {
      setEvent(updated);
      setClientId(updated.client_id);
      setProjectId(updated.project_id);
      setBillable(updated.billable);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not save');
    },
  });

  const toggleDone = useMutation({
    mutationFn: (nextDone: boolean) =>
      api<PlanEvent>(`/api/calendars/events/${event.id}/done`, {
        method: 'PATCH',
        json: { done: nextDone },
      }),
    onSuccess: (updated) => {
      setEvent(updated);
      invalidate();
      toast.show(updated.completed_at ? 'Marked complete' : 'Reopened', {
        tone: 'success',
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update');
    },
  });

  const toggleBusy = useMutation({
    mutationFn: (counts_as_busy: boolean) =>
      api<PlanEvent>(`/api/calendars/events/${event.id}/busy`, {
        method: 'PATCH',
        json: { counts_as_busy },
      }),
    onSuccess: (updated) => {
      setEvent(updated);
      invalidate();
      toast.show(
        updated.counts_as_busy
          ? updated.is_recurring
            ? 'Marked busy (this and future)'
            : 'Marked as busy'
          : updated.is_recurring
            ? 'Marked free (this and future)'
            : 'Marked as not busy',
        { tone: 'success' },
      );
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update');
    },
  });

  const addPrep = useMutation({
    mutationFn: (minutes: number) =>
      api<Task>(`/api/calendars/events/${event.id}/prep`, {
        method: 'POST',
        json: { minutes },
      }),
    onSuccess: (task) => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      toast.show(`Added “${task.title}” to your backlog`, { tone: 'success' });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not add prep time');
    },
  });

  const clientProjects = projects.data ?? [];

  return (
    <Sheet title={event.title || '(No title)'} onClose={onClose}>
      <div className="flex flex-col gap-6">
        {needsCompleteNudge && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-3"
          >
            <AlertCircle
              size={18}
              strokeWidth={1.75}
              className="mt-0.5 shrink-0 text-warning"
              aria-hidden
            />
            <div className="flex flex-1 flex-col gap-2">
              <p className="text-small text-text">
                This event already ended. Mark it complete when you are done with
                it.
              </p>
              <Button
                variant="secondary"
                loading={toggleDone.isPending}
                onClick={() => toggleDone.mutate(true)}
              >
                Mark complete
              </Button>
            </div>
          </div>
        )}

        <section className="flex flex-col gap-2">
          <p className="text-body text-text">{formatRange(event, timezone)}</p>
          <p className="text-small text-text-tertiary">
            {eventShowsAsBusy(event) ? 'Busy' : 'Free'}
            {event.all_day ? ' · All-day' : null}
            {event.is_recurring ? ' · Recurring' : null}
            {done ? ' · Done' : ' · To do'}
            {event.billable ? ' · Billable' : null}
          </p>
          {event.location ? (
            <p className="text-small text-text-secondary">
              <EventLocation location={event.location} />
            </p>
          ) : null}
          {event.starts_tz ? (
            <p className="text-small text-text-tertiary">
              Event zone · {event.starts_tz.replace(/_/g, ' ')}
            </p>
          ) : null}
        </section>

        {event.description ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-small font-medium text-text-secondary">Notes</h3>
            <p className="whitespace-pre-wrap rounded-lg border border-border bg-surface-2/60 px-3 py-3 text-small text-text-secondary">
              {event.description}
            </p>
          </section>
        ) : null}

        <section className="flex flex-col gap-3">
          <h3 className="text-small font-medium text-text-secondary">Work</h3>

          <div className="flex flex-col gap-2">
            <label htmlFor="event-client" className="text-small text-text-tertiary">
              Client
            </label>
            <select
              id="event-client"
              value={clientId ?? ''}
              disabled={saveWork.isPending}
              onChange={(e) => {
                const next = e.target.value ? Number(e.target.value) : null;
                setClientId(next);
                setProjectId(null);
                saveWork.mutate({ client_id: next, project_id: null });
              }}
              className="min-h-touch w-full rounded border border-border bg-surface-1 px-3 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
            >
              <option value="">No client</option>
              {clients.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="event-project" className="text-small text-text-tertiary">
              Project
            </label>
            <select
              id="event-project"
              value={projectId ?? ''}
              disabled={!clientId || saveWork.isPending}
              onChange={(e) => {
                const next = e.target.value ? Number(e.target.value) : null;
                setProjectId(next);
                const project = clientProjects.find((p) => p.id === next);
                const nextBillable =
                  next && project ? Boolean(project.default_billable) : billable;
                setBillable(nextBillable);
                saveWork.mutate({
                  project_id: next,
                  ...(next ? { billable: nextBillable } : {}),
                });
              }}
              className="min-h-touch w-full rounded border border-border bg-surface-1 px-3 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border disabled:opacity-50"
            >
              <option value="">{clientId ? 'No project' : 'Pick a client first'}</option>
              {clientProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <label className="flex min-h-touch items-center gap-3">
            <input
              type="checkbox"
              checked={billable}
              disabled={saveWork.isPending}
              onChange={(e) => {
                const next = e.target.checked;
                setBillable(next);
                saveWork.mutate({ billable: next });
              }}
              className="size-icon accent-[var(--accent)]"
            />
            <span className="text-body text-text">Billable</span>
          </label>

          {event.is_recurring ? (
            <p className="text-small text-text-tertiary">
              Client, project, and billable apply to this and later occurrences.
            </p>
          ) : null}
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-small font-medium text-text-secondary">Status</h3>

          <button
            type="button"
            onClick={() => toggleDone.mutate(!done)}
            disabled={toggleDone.isPending}
            className={cn(
              'pressable flex min-h-touch items-center gap-3 rounded-lg border px-3 text-left',
              done
                ? 'border-accent/40 bg-accent/10 text-text'
                : 'border-border bg-surface-1 text-text',
            )}
          >
            <span
              className={cn(
                'flex size-5 items-center justify-center rounded border',
                done
                  ? 'border-accent bg-accent text-text-on-accent'
                  : 'border-border bg-surface-2',
              )}
              aria-hidden
            >
              {done ? <Check size={12} strokeWidth={3} /> : null}
            </span>
            <span className="flex-1 text-body">
              {done ? 'Completed' : 'Mark as done'}
            </span>
          </button>

          {event.calendar_is_busy_source ? (
            <div className="flex flex-col gap-2">
              <Button
                variant="secondary"
                loading={toggleBusy.isPending}
                onClick={() => toggleBusy.mutate(!event.counts_as_busy)}
              >
                {event.counts_as_busy ? 'Mark as not busy' : 'Mark as busy'}
              </Button>
              <p className="text-small text-text-tertiary">
                {event.is_recurring
                  ? 'Busy applies to this occurrence and all later ones in the series.'
                  : event.all_day
                    ? 'All-day events stay free unless you mark them busy (vacations, holidays).'
                    : 'Not busy frees the slot so Chronos can schedule over it.'}
              </p>
            </div>
          ) : (
            <p className="text-small text-text-tertiary">
              This calendar is not a busy source, so its events always appear as
              free on Plan and do not block scheduling.
            </p>
          )}
        </section>

        {eventShowsAsBusy(event) && !done && (
          <section className="flex flex-col gap-3">
            <h3 className="text-small font-medium text-text-secondary">Prep time</h3>
            <p className="text-small text-text-tertiary">
              Adds a task to your backlog so getting ready for this has its own
              slot. Drag it anywhere on the day you like.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {PREP_PRESETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={prepMinutes === m}
                  onClick={() => setPrepMinutes(m)}
                  className={cn(
                    'pressable min-h-touch rounded-full border px-3 text-small tabular',
                    prepMinutes === m
                      ? 'border-accent bg-accent-quiet text-accent'
                      : 'border-border bg-surface-1 text-text-secondary',
                  )}
                >
                  {m}m
                </button>
              ))}
              <span className="flex min-h-touch items-center gap-2">
                <input
                  type="number"
                  aria-label="Prep minutes"
                  min={1}
                  max={480}
                  step={5}
                  value={prepMinutes}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (Number.isFinite(next)) setPrepMinutes(next);
                  }}
                  className="tabular min-h-touch w-20 rounded border border-border bg-surface-1 px-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
                />
                <span className="text-small text-text-tertiary">min</span>
              </span>
            </div>

            <Button
              variant="secondary"
              loading={addPrep.isPending}
              disabled={prepMinutes < 1 || prepMinutes > 480}
              onClick={() => addPrep.mutate(prepMinutes)}
            >
              Add prep time
            </Button>
          </section>
        )}

        {!event.all_day && (
          <section className="flex flex-col gap-3">
            <h3 className="text-small font-medium text-text-secondary">Time tracking</h3>
            {focus.data?.active_calendar_event_id === event.id ? (
              <div className="flex flex-col gap-2">
                <p className="tabular text-body">
                  {formatClock(focus.data.task_elapsed_seconds)}
                  {scheduledMinutes != null && ` / ${formatDuration(scheduledMinutes)} scheduled`}
                </p>
                <Button variant="secondary" onClick={() => focusControls.pauseTask.mutate()}>
                  Stop meeting timer
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={() =>
                  focusControls.startEvent.mutate({ calendar_event_id: event.id })
                }
              >
                Start meeting timer
              </Button>
            )}
            <p className="text-small text-text-tertiary">
              Compare how long the meeting actually ran versus its calendar slot.
            </p>
          </section>
        )}
      </div>
    </Sheet>
  );
}
